import { createFileRoute, Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { Banknote, Copy, Mail, MessageCircle, ArrowLeft, CheckCircle2, Clock, Printer, FileDown, XCircle, AlertTriangle, Send, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getInvoiceByReference } from "@/lib/api/credits.functions";
import { createPayfastCheckout } from "@/lib/api/payfast.functions";
import { resendInvoiceEmail } from "@/lib/api/invoice-resend.functions";
import { site } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { PayFastCheckoutButton } from "@/components/site/PayFastCheckoutButton";
import { supabase } from "@/integrations/supabase/client";


export const Route = createFileRoute("/invoice/$ref")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { payfast?: "return" | "cancel" } => ({
    payfast:
      search.payfast === "return" || search.payfast === "cancel"
        ? (search.payfast as "return" | "cancel")
        : undefined,
  }),
  head: ({ params }) => ({
    meta: [
      { title: `Invoice ${params.ref} — Pay by EFT | Nkadimeng Danny Legal` },
      { name: "description", content: "Pay your order via EFT using our banking details. Reference required." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: InvoicePage,
  errorComponent: ({ error }) => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">Could not load invoice: {error.message}</p>
      <Link to="/" className="mt-4 inline-block text-navy underline">Back to home</Link>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">Invoice not found.</p>
      <Link to="/" className="mt-4 inline-block text-navy underline">Back to home</Link>
    </div>
  ),
});

function InvoicePage() {
  const { ref } = useParams({ from: "/invoice/$ref" });
  const search = useSearch({ from: "/invoice/$ref" });
  const fetchInvoice = useServerFn(getInvoiceByReference);
  const qc = useQueryClient();
  const resend = useServerFn(resendInvoiceEmail);
  const [resending, setResending] = useState(false);
  const [viewerEmail, setViewerEmail] = useState<string>("");
  useEffect(() => {
    supabase.auth.getUser().then((r) => setViewerEmail(r.data.user?.email ?? ""));
  }, []);


  async function handleResend() {
    setResending(true);
    try {
      const r = await resend({ data: { reference: ref } });
      if (r.ok) {
        toast.success("Invoice email resent", {
          description: `Sent to ${r.sentTo}`,
        });
      } else if (r.reason === "already_paid") {
        toast.info(r.message ?? "This invoice is already paid.");
      } else {
        toast.error("Could not resend invoice email", {
          description: r.reason ?? "Please try again later",
        });
      }
    } catch (e: any) {
      toast.error("Resend failed", { description: e?.message ?? String(e) });
    } finally {
      setResending(false);
    }
  }
  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice", ref],
    queryFn: () => fetchInvoice({ data: { reference: ref } }),
    retry: false,
    // Re-poll every 8s while awaiting payment so PayFast confirmations show
    // up promptly even if realtime is blocked by the network.
    refetchInterval: (q) =>
      (q.state.data as any)?.order?.status === "paid" ? false : 8000,
  });

  // Realtime: subscribe to credit_orders + contract_orders rows for this
  // reference and re-fetch when PayFast marks the order paid.
  useEffect(() => {
    if (!ref) return;
    const channel = supabase
      .channel(`invoice-${ref}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "credit_orders",
          filter: `reference=eq.${ref}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["invoice", ref] });
          toast.success("Payment status updated");
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "contract_orders",
          filter: `reference=eq.${ref}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["invoice", ref] });
          toast.success("Payment status updated");
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [ref, qc]);


  // Auto-launch PayFast checkout once on first load of a pending invoice
  // so the buyer lands straight on the payment page. We skip the auto-launch
  // if PayFast just bounced them back ("return" or "cancel") to avoid loops.
  const launchPayfast = useServerFn(createPayfastCheckout);
  const launchedRef = useRef(false);
  const [retrying, setRetrying] = useState(false);

  async function openPayfastCheckout() {
    if (!ref) return;
    setRetrying(true);
    try {
      const { processUrl, fields } = await launchPayfast({
        data: { reference: ref },
      });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = processUrl;
      form.style.display = "none";
      for (const [k, v] of Object.entries(fields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v as string;
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
    } catch (e: any) {
      toast.error("Could not open PayFast", {
        description: e?.message ?? String(e),
      });
      setRetrying(false);
    }
  }

  useEffect(() => {
    if (launchedRef.current) return;
    if (!data || !ref) return;
    const status = (data as any)?.order?.status;
    if (status !== "pending") return;
    if (search.payfast === "return" || search.payfast === "cancel") return;
    launchedRef.current = true;
    void openPayfastCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ref, search.payfast]);


  if (isLoading) return <div className="container-narrow py-16 text-sm text-muted-foreground">Loading invoice…</div>;
  if (error) return (
    <div className="container-narrow py-16 text-center">
      <p className="text-muted-foreground">{(error as Error).message}</p>
      <Link to="/" className="mt-4 inline-block text-navy underline">Back to home</Link>
    </div>
  );
  if (!data) return null;

  const o = data.order;
  const isPaid = o.status === "paid";
  const whatsappMsg = encodeURIComponent(`Hi, I'm submitting proof of payment for invoice ${o.reference} (R${o.amountZar.toFixed(2)}). Please verify so I can access my order.`);
  const mailto = `mailto:${site.email}?subject=${encodeURIComponent("Proof of Payment — " + o.reference)}&body=${whatsappMsg}`;

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${label}`));
  }

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-navy/5 to-transparent py-10">
        <div className="container-narrow">
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Invoice</p>
              <h1 className="font-display text-3xl text-navy">{o.reference}</h1>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {site.name} · Reg {site.registration.number}
                {site.registration.taxNumber
                  ? ` · Tax ${site.registration.taxNumber}`
                  : ""}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex flex-wrap items-center gap-2">
                {isPaid ? (
                  (o as any).paymentMethod === "payfast" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Paid via PayFast
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Paid
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                    <Clock className="h-3.5 w-3.5" /> Pending — awaiting payment
                  </span>
                )}
                {isPaid && (o as any).receiptSentAt && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-medium text-sky-800">
                    Receipt emailed
                  </span>
                )}
                {isPaid && (o as any).publicToken && (
                  <a
                    href={`/api/public/receipt?ref=${encodeURIComponent(o.reference)}&t=${encodeURIComponent((o as any).publicToken)}`}
                    className="inline-flex items-center gap-1 rounded-md bg-gold px-3 py-1.5 text-xs font-semibold text-gold-foreground hover:brightness-95"
                  >
                    <FileDown className="h-3.5 w-3.5" /> Download receipt PDF
                  </a>
                )}

                <Link
                  to="/payment-status/$ref"
                  params={{ ref: o.reference }}
                  className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
                >
                  <Activity className="h-3.5 w-3.5" /> Track status
                </Link>
                {!isPaid && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResend}
                    disabled={resending}
                  >
                    {resending ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="mr-1 h-3.5 w-3.5" />
                    )}
                    Resend invoice email
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="mr-1 h-3.5 w-3.5" /> Print
                </Button>
              </div>
              {isPaid && o.paidAt && (
                <p className="text-[11px] text-muted-foreground">
                  Paid {new Date(o.paidAt).toLocaleString("en-ZA")}
                  {(o as any).paidProviderRef
                    ? ` · PayFast ref ${(o as any).paidProviderRef}`
                    : ""}
                </p>
              )}
              {!isPaid && (
                <p className="text-[11px] text-muted-foreground">
                  Status updates live — keep this tab open after paying.
                </p>
              )}
            </div>

          </div>
        </div>
      </section>

      {(search.payfast === "return" || search.payfast === "cancel") && (
        <section className="container-narrow -mt-4">
          {search.payfast === "return" ? (
            isPaid ? (
              <div className="flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">Payment confirmed via PayFast.</p>
                  <p className="mt-0.5 text-[12px]">
                    Your order is unlocked. A branded receipt has been emailed and is available below.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-xl border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 animate-pulse" />
                <div>
                  <p className="font-semibold">Thanks — PayFast is finalising your payment.</p>
                  <p className="mt-0.5 text-[12px]">
                    This page will update automatically the moment PayFast confirms (usually a few seconds). No need to refresh.
                  </p>
                </div>
              </div>
            )
          ) : (
            <div className="flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">Payment cancelled — no money was taken.</p>
                <p className="mt-0.5 text-[12px]">
                  You cancelled at the PayFast checkout. Your invoice is still open — retry below or pay by EFT.
                </p>
                {!isPaid && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={openPayfastCheckout}
                      disabled={retrying}
                      className="bg-rose-600 text-white hover:bg-rose-700"
                    >
                      {retrying ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Banknote className="mr-1 h-3.5 w-3.5" />
                      )}
                      Retry payment with PayFast
                    </Button>
                    <Link
                      to="/payment-status/$ref"
                      params={{ ref: o.reference }}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-50"
                    >
                      <Activity className="h-3.5 w-3.5" /> View status
                    </Link>
                  </div>
                )}
              </div>
              {!isPaid && (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              )}
            </div>
          )}
        </section>
      )}


      <section className="py-8">
        <div className="container-narrow grid gap-6 md:grid-cols-2 print:grid-cols-2">
          {!isPaid && (
            <div className="md:col-span-2 rounded-2xl border-2 border-[#13B5EA]/30 bg-[#13B5EA]/5 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#0E7CB0]">
                    Recommended · Instant
                  </p>
                  <h2 className="mt-1 font-display text-lg text-navy">
                    Pay R{o.amountZar.toFixed(2)} by card, Instant EFT, or SnapScan via PayFast
                  </h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Secure South-African processor. Your order unlocks
                    automatically the moment payment clears — no proof of
                    payment needed.
                  </p>
                </div>
                <div className="w-full md:w-auto md:min-w-[260px]">
                  <PayFastCheckoutButton reference={o.reference} />
                </div>
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-lg text-navy">Order summary</h2>
            <div className="mt-4 divide-y divide-border">
              {o.lineItems.map((li, i) => (
                <div key={i} className="flex items-center justify-between py-3 text-sm">
                  <span>{li.label} {li.qty > 1 && <span className="text-muted-foreground">× {li.qty}</span>}</span>
                  <span className="font-semibold">R{li.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3 text-base font-bold">
                <span>Total due</span>
                <span>R{o.amountZar.toFixed(2)}</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Created {new Date(o.createdAt).toLocaleString("en-ZA")}
            </p>
          </div>

          <div className="rounded-2xl border border-gold/40 bg-secondary/30 p-6">
            <div className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-navy" />
              <h2 className="font-display text-lg text-navy">EFT banking details</h2>
            </div>
            <dl className="mt-4 space-y-2 text-sm">
              <Row label="Account name" value={site.banking.accountName} onCopy={(v) => copy(v, "account name")} />
              <Row label="Bank" value={`${site.banking.bank} (${site.banking.accountType})`} />
              <Row label="Account number" value={site.banking.accountNumber} onCopy={(v) => copy(v, "account number")} mono />
              <Row label="Branch code" value={site.banking.branchCode} onCopy={(v) => copy(v, "branch code")} mono />
              <Row label="Use as reference" value={o.reference} onCopy={(v) => copy(v, "reference")} mono highlight />
            </dl>
            <div className="mt-5 rounded-md bg-navy/5 p-3 text-xs text-foreground/80">
              After paying, send proof of payment so we can release your{" "}
              {data.kind === "credit" ? "credits" : "document"} immediately.
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={mailto} className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-xs hover:border-navy">
                <Mail className="h-3.5 w-3.5" /> Email POP
              </a>
              <a href={`https://wa.me/${site.whatsapp}?text=${whatsappMsg}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 hover:bg-emerald-100">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp POP
              </a>
            </div>
          </div>
        </div>

        {isPaid && data.kind === "credit" && (
          <div className="container-narrow mt-6">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              ✅ This pack is paid — your credits have been added.{" "}
              <Link to={"/portal/credits" as any} className="font-semibold underline">View your balance</Link>.
            </div>
          </div>
        )}
        {isPaid && data.kind === "contract" && (
          <div className="container-narrow mt-6">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              ✅ Payment received — check your email for the unlock password and download link.
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value, onCopy, mono, highlight }: { label: string; value: string; onCopy?: (v: string) => void; mono?: boolean; highlight?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`flex items-center gap-2 ${mono ? "font-mono" : ""} ${highlight ? "font-bold text-navy" : ""}`}>
        <span>{value}</span>
        {onCopy && (
          <button type="button" onClick={() => onCopy(value)} className="text-muted-foreground hover:text-navy" aria-label={`Copy ${label}`}>
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
  );
}
